import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Constants } from "@constants";
import { IPaginadoDTO, IPremioDTO, IPremioLimiteDTO, PaginadoDTO, PremioDTO, PremioLimiteDTO } from "@DTOS";
import { map } from "rxjs/operators";
import { firstValueFrom } from "rxjs";

@Injectable({
    providedIn: "root"
})
export class PremiosServiceBackend {
    constructor(protected httpClient: HttpClient) {}

    public async getPremios(pagina: number, cantidad: number): Promise<PaginadoDTO<PremioDTO>> {
        const response = await firstValueFrom(
            this.httpClient.get<IPaginadoDTO<IPremioDTO>>(`${Constants.apiURL}/prizes`, {
                params: {
                    page: pagina.toFixed(),
                    size: cantidad.toFixed()
                }
            })
        );

        const premios = response.results?.map(premio => new PremioDTO(premio)) ?? [];
        return new PaginadoDTO({...response, results: premios});
    }

    public async getAllPremios(): Promise<PremioDTO[]> {
        return await firstValueFrom(
            this.httpClient.get<IPremioDTO[]>(`${Constants.apiURL}/prizes`).pipe(
                map(response => response.map(premio => new PremioDTO(premio)))
            )
        );
    }

    public async getPremiosQuery(
        pagina: number, cantidad: number, paises: string[], campanias: string[]
    ): Promise<PaginadoDTO<PremioDTO>> {
        const params: any = {
            page: pagina.toFixed(),
            size: cantidad.toFixed()
        };

        if (paises && paises.length) {
            params.country = paises;
        }

        if (campanias && campanias.length) {
            params.campaign = campanias;
        }

        const response = await firstValueFrom(
            this.httpClient.get<IPaginadoDTO<IPremioDTO>>(
                `${Constants.apiURL}/prizes`, { params }
            )
        );

        const premios = response.results.map(premio => new PremioDTO(premio));
        return new PaginadoDTO({...response, results: premios});
    }

    public async getPremio(id: number): Promise<PremioDTO> {
        const response = await firstValueFrom(this.httpClient.get<IPremioDTO>(`${Constants.apiURL}/prizes/${id}`));

        return new PremioDTO(response);
    }

    public async getPremioByDollars(dollar_price: number): Promise<PremioDTO> {
        const response = await firstValueFrom(
            this.httpClient.get<IPremioDTO>(`${Constants.apiURL}/prizes`, {
                params: Object.entries({
                    dollar_price
                }).reduce((a, [k, v]) => {
                    if (v == null) {
                        return a;
                    } else {
                        a[k] = v;
                        return a;
                    }
                }, {})
            })
        );

        return new PremioDTO(response);
    }

    public async postPremio(premio: PremioDTO): Promise<PremioDTO> {

        const response = await firstValueFrom(
            this.httpClient.post<IPremioDTO>(
                `${Constants.apiURL}/prizes`, premio.toJSON()
            )
        );

        return new PremioDTO(response);
    }

    public async patchPremio(premio: PremioDTO): Promise<PremioDTO> {
        const response = await firstValueFrom(
            this.httpClient.patch<IPremioDTO>(
                `${Constants.apiURL}/prizes/${premio.id}`, premio.toJSON()
            )
        );

        return new PremioDTO(response);
    }

    public async deletePremio(premio: PremioDTO): Promise<void> {
        return await firstValueFrom(this.httpClient.delete<void>(`${Constants.apiURL}/prizes/${premio.id}`));
    }

    public async deleteImagenPremio(premio: PremioDTO): Promise<void> {
        if (premio.imagenURL) {
            return await firstValueFrom(
                this.httpClient
                    .delete<void>(`${Constants.apiURL}/${premio.imagenURL}`)
            );
        }
    }

    public async getLimitesAutomaticos(): Promise<PremioLimiteDTO> {
        const response = await firstValueFrom(this.httpClient.get<IPremioLimiteDTO>(`${Constants.apiURL}/prize-balance`));
        return new PremioLimiteDTO(response[0]);
    }

    public async patchLimites(limites: PremioLimiteDTO): Promise<PremioLimiteDTO> {
        const response = await firstValueFrom(
            this.httpClient.patch<IPremioLimiteDTO>(
                `${Constants.apiURL}/prize-balance/1`, limites.toJSON()
            )
        );

        return new PremioLimiteDTO(response);
    }

}
